import * as UI from "@iyk/ui"
import { FanClip } from "@iyk/vf-core"
import { Res } from "@iyk/vf-server"
import { Array, Effect, Option } from "effect"
import * as React from "react"
import { appConfig } from "../../app-config.ts"
import { Handler } from "../../server/handler.ts"
import { Profile } from "../profile.ts"
import { AlertCard } from "../ui/alert-card.tsx"
import { ExclusiveCardList } from "../ui/exclusive-card-list.tsx"
import { ExclusiveDiscountCode } from "../ui/exclusive-discount-code.tsx"
import { ExclusiveEmptyCard } from "../ui/exclusive-empty-card.tsx"
import { FanClipsUploadCard } from "../ui/fan-clips-upload-card.tsx"
import { FanClipsViewCard } from "../ui/fan-clips-view-card.tsx"
import { HowToTapDialog } from "../ui/how-to-tap-dialog.tsx"
import { Link, Routes } from "../ui/link.tsx"
import { LockedExclusiveCard } from "../ui/locked-exclusive-card.tsx"
import { ShowScheduleLink } from "../ui/show-schedule-item.tsx"
import { UserProfileCard } from "../ui/user-profile-card.tsx"
import type { Route } from "./+types/home.ts"

type LoaderData = {
  nextShow?: Profile.Show
  exclusives: Array<Profile.Exclusive>
  totalShows: number
  discountCode: { code: string; shopUrl?: string } | null
  checkInPrompt?: Profile.CheckInPromptInfo
  lockedExclusives: Array<Profile.LockedExclusive>
  displayFanClipsFeedCard: boolean
}

export const loader = Handler.make<LoaderData>(
  "profile-home",
  ({ api, fanClips, sessions, discountCodes, clock, shows }) => {
    return Effect.all(
      {
        checkInPrompt: api.checkInPrompt(sessions).pipe(Effect.map(Option.getOrUndefined)),
        nextShow: api.findNextShow,
        totalShows: api.listShows.pipe(Effect.map(Array.length)),
        discountCode: Effect.gen(function* () {
          const user = yield* sessions.authenticated.pipe(Effect.map((session) => session.user))
          const isMerchDiscountActive = yield* clock.now.pipe(
            Effect.map((now) => now.epochMillis < 1760932800000) // October 20, 2025 12:00:00 AM EST
          )
          return yield* Effect.if(isMerchDiscountActive, {
            onTrue: () =>
              shows.findCheckInsByUserId(user.id).pipe(
                Effect.map((checkIns) =>
                  Array.isEmptyArray(checkIns)
                    ? null
                    : {
                        code: "BRINGME10",
                        shopUrl:
                          "https://shop.bmthofficial.com/?utm_source=website-3rdparty&utm_medium=referral&utm_campaign=BMTHUStour290925&utm_id=IY"
                      }
                )
              ),
            onFalse: () =>
              discountCodes.findByUserId(user.id).pipe(
                Effect.map((code) => ({ code: code.value.code, shopUrl: appConfig.shopUrl ?? undefined })),
                Effect.orElseSucceed(() => null)
              )
          })
        }).pipe(Effect.orElseSucceed(() => null)),
        exclusives: sessions.authenticated.pipe(
          Effect.flatMap((session) => api.findUnlockedExclusives(session.user.id)),
          Effect.orElseSucceed(() => [])
        ),
        lockedExclusives: sessions.authenticated.pipe(
          Effect.flatMap((session) => api.findLockedExclusives(Option.some(session.user.id))),
          Effect.orElse(() => api.findLockedExclusives(Option.none()))
        ),
        displayFanClipsFeedCard: fanClips
          .count({ visible: FanClip.Visible.unsafeFrom(true) })
          .pipe(Effect.map((count) => count > 0))
      },
      { concurrency: "unbounded" }
    ).pipe(Effect.map(Res.json))
  }
)

export default function ProfileRoute(props: Route.ComponentProps) {
  const layoutData = props.matches[1].data

  return layoutData.user ? (
    <LoggedInProfile
      user={layoutData.user}
      exclusives={props.loaderData.exclusives}
      nextShow={props.loaderData.nextShow}
      totalShows={props.loaderData.totalShows}
      discountCode={props.loaderData.discountCode}
      checkInPrompt={props.loaderData.checkInPrompt}
      lockedExclusives={props.loaderData.lockedExclusives}
      displayPreShowPlaylist={layoutData.user?.ownedMerchSkuIds?.length > 0}
      displayFanClipsFeedCard={props.loaderData.displayFanClipsFeedCard}
    />
  ) : (
    <LoggedOutProfile
      nextShow={props.loaderData.nextShow}
      totalShows={props.loaderData.totalShows}
      checkInPrompt={props.loaderData.checkInPrompt}
      lockedExclusives={props.loaderData.lockedExclusives}
      displayFanClipsFeedCard={props.loaderData.displayFanClipsFeedCard}
    />
  )
}

const LoggedInProfile = ({
  user,
  nextShow,
  totalShows,
  alert,
  exclusives,
  checkInPrompt,
  lockedExclusives,
  discountCode,
  displayPreShowPlaylist,
  displayFanClipsFeedCard
}: LoggedInProfile.Props) => (
  <div className="flex flex-col gap-8 px-4">
    <div className="flex flex-col gap-4">
      <UserProfileCard userId={user.id} username={user.username} totalProofs={user.totalProofs} />
      {alert && (
        <AlertCard
          eyebrow={alert.eyebrow}
          title={alert.title}
          subtitle={alert.subtitle}
          announcementTitle={alert.announcementTitle}
          announcementDate={alert.announcementDate}
          description={alert.description}
          kind={alert.kind}
        />
      )}
      {checkInPrompt && (
        <CheckInCard
          title={`At the ${checkInPrompt.show.venue} show?`}
          description="Win an item used on stage by Bring Me The Horizon. Submit your footage from the band's set for a chance to be featured in their fan-made round up video and socials+"
        />
      )}
      <FanClipsUploadCard
        title="Win an item used on stage"
        description="Submit your footage from the band's set for a chance to be featured in their fan-made round up video and socials+"
        canUpload={user.attendedShowIds.length > 0}
      />
      {displayFanClipsFeedCard && (
        <FanClipsViewCard
          title="Watch featured clips from fans on the ground at the USA Ascension Program+ tour"
          images={[
            `${appConfig.s3.assets.appUrl}/fan_clip_asset_1.jpg`,
            `${appConfig.s3.assets.appUrl}/fan_clip_asset_2.jpg`,
            `${appConfig.s3.assets.appUrl}/fan_clip_asset_3.jpg`,
            `${appConfig.s3.assets.appUrl}/fan_clip_asset_4.jpg`
          ]}
        />
      )}
    </div>
    <section className="flex flex-col gap-4">
      <h2 className="text-base">Your Unlocks</h2>
      <div className="grid gap-2">
        {Array.isEmptyArray(exclusives) && Array.isEmptyArray(lockedExclusives) && !displayPreShowPlaylist ? (
          <ExclusiveEmptyCard />
        ) : null}
        {displayPreShowPlaylist && <PreShowPlaylistCard />}
        {lockedExclusives.map((lockedExclusive) => (
          <LockedExclusiveCard key={lockedExclusive.id} lockedExclusive={lockedExclusive} />
        ))}
        {discountCode && <ExclusiveDiscountCode code={discountCode.code} shopUrl={discountCode.shopUrl} />}
        {!Array.isEmptyArray(exclusives) && <ExclusiveCardList owned={true} exclusives={exclusives} />}
      </div>
    </section>
    {nextShow && <NextShow nextShow={nextShow} showCount={totalShows} />}
  </div>
)

namespace LoggedInProfile {
  export interface Props {
    user: Profile.User
    exclusives: Array<Profile.Exclusive>
    totalShows: number
    nextShow?: Profile.Show
    alert?: AlertCard.Props
    checkInPrompt?: Profile.CheckInPromptInfo
    lockedExclusives: Array<Profile.LockedExclusive>
    discountCode: { code: string; shopUrl?: string } | null
    displayPreShowPlaylist: boolean
    displayFanClipsFeedCard: boolean
  }
}

// TODO: add login button and auth sheet
const LoggedOutProfile = ({
  nextShow,
  totalShows,
  checkInPrompt,
  lockedExclusives,
  displayFanClipsFeedCard
}: LoggedOutProfile.Props) => {
  const [openHowToTapDialog, setOpenHowToTapDialog] = React.useState(false)

  return (
    <div className="flex flex-col gap-8 px-4">
      <div className="flex flex-col gap-4">
        <section
          className={UI.cx(
            "flex flex-col items-center justify-center aspect-video",
            "p-8 text-sm text-center text-balance relative bg-cover rounded",
            "border border-white/[10%] bg-white/[3%]"
          )}
        >
          <h1>Become a verified fan</h1>
          <p className="text-gray-11">Claim your VIP pass or attend shows to begin</p>
        </section>
        {appConfig.assets.images.howToTap && (
          <HowToTapDialog
            image={appConfig.assets.images.howToTap}
            open={openHowToTapDialog}
            setOpen={setOpenHowToTapDialog}
          />
        )}
        {checkInPrompt && (
          <CheckInCard
            title={`At the ${checkInPrompt.show.venue} show?`}
            description="Win an item used on stage by Bring Me The Horizon. Submit your footage from the band's set for a chance to be featured in their fan-made round up video and socials+"
          />
        )}
        {displayFanClipsFeedCard && (
          <FanClipsViewCard
            title="Watch featured clips from fans on the ground at the USA Ascension Program+ tour"
            images={[
              `${appConfig.s3.assets.appUrl}/fan_clip_asset_1.jpg`,
              `${appConfig.s3.assets.appUrl}/fan_clip_asset_2.jpg`,
              `${appConfig.s3.assets.appUrl}/fan_clip_asset_3.jpg`,
              `${appConfig.s3.assets.appUrl}/fan_clip_asset_4.jpg`
            ]}
          />
        )}
      </div>
      {!Array.isEmptyArray(lockedExclusives) && (
        <section className="flex flex-col gap-4">
          <h2 className="text-base">Unlocks</h2>
          <div className="grid gap-2">
            {lockedExclusives.map((lockedExclusive) => (
              <LockedExclusiveCard key={lockedExclusive.id} lockedExclusive={lockedExclusive} />
            ))}
          </div>
        </section>
      )}

      {nextShow && (
        <section className="flex flex-col gap-4">
          <div className="flex justify-between items-center">
            <h2 className="text-base">Next show</h2>
            <Link to={Routes.tourSchedule} className={UI.classForLink({ className: "text-sm" })}>
              View all ({totalShows})
            </Link>
          </div>
          {nextShow && (
            <ShowScheduleLink
              id={nextShow.id}
              city={nextShow.city}
              state={nextShow.state}
              dayOfMonth={nextShow.dayOfMonth}
              dayOfWeek={nextShow.dayOfWeek}
              time={nextShow.time}
              venue={nextShow.venue}
              url={nextShow.url}
              month={nextShow.month}
              checked={false}
            />
          )}
        </section>
      )}
    </div>
  )
}

namespace LoggedOutProfile {
  export interface Props {
    nextShow?: Profile.Show
    totalShows: number
    checkInPrompt?: Profile.CheckInPromptInfo
    lockedExclusives: Array<Profile.LockedExclusive>
    displayFanClipsFeedCard: boolean
  }
}

const NextShow = ({ nextShow, showCount }: NextShow.Props) => (
  <section className="flex flex-col gap-4">
    <div className="flex justify-between items-center">
      <h2 className="text-base">Next show</h2>
      <Link to={Routes.tourSchedule} className={UI.classForLink({ className: "text-sm" })}>
        View all ({showCount})
      </Link>
    </div>
    {nextShow && (
      <ShowScheduleLink
        id={nextShow.id}
        city={nextShow.city}
        state={nextShow.state}
        dayOfMonth={nextShow.dayOfMonth}
        dayOfWeek={nextShow.dayOfWeek}
        time={nextShow.time}
        venue={nextShow.venue}
        url={nextShow.url}
        month={nextShow.month}
        checked={false}
      />
    )}
  </section>
)

namespace NextShow {
  export interface Props {
    nextShow?: Profile.Show
    showCount: number
  }
}

const CheckInCard = ({ title, description }: CheckInCard.Props) => (
  <section className="flex flex-col gap-4 p-4 bshadow bshadow-gray-5">
    <div className="grid gap-1">
      <h3 className="text-sm text-gray-12">{title}</h3>
      <p className="text-sm text-gray-11">{description}</p>
    </div>
    <Link
      to={Routes.checkIn}
      className={UI.classForButton({ size: "sm", kind: "solid", className: "w-max" })}
    >
      Check in
    </Link>
  </section>
)

namespace CheckInCard {
  export interface Props {
    title: string
    description: string
  }
}

const PreShowPlaylistCard = () => {
  return (
    <div className="flex h-36 overflow-hidden rounded bg-grayA-2">
      <img
        src="https://assets.iyk.app/bmth/pre-show-playlist.jpg"
        alt="pre show playlist"
        className="aspect-square object-cover shrink-0"
      />
      <div className="flex-1 flex flex-col justify-between items-start gap-4 p-4">
        <div>
          <p className="text-sm">PRE SHOW PLAYLIST</p>
          <p className="text-sm text-gray-11">12 tracks</p>
        </div>
        <a
          href="https://open.spotify.com/playlist/2AupPgcwNfiEkJEoCx2XNg?si=ZxnZx4H5RXyigXGefePnhA"
          target="_blank"
          rel="noreferrer"
          className={UI.classForButton({ size: "sm", kind: "solid" })}
        >
          <UI.Icon.Spotify />
          Listen on Spotify
        </a>
      </div>
    </div>
  )
}
